import { Fragment } from "react";
import {
  type Cell,
  type ColumnDef,
  type Row,
  type Table as TableType,
  flexRender,
} from "@tanstack/react-table";
import {
  type TableVariantProps,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../table";
import { DataTablePagination } from "./data-table-pagination";
import { Icon } from "../icon";
import { cn } from "~/lib/utils";
import { Skeleton } from "../skeleton";

export type DataTableProps<TData, TValue> = TableVariantProps & {
  table: TableType<TData>;
  columns?: ColumnDef<TData, TValue>[];
  onRowClick?: (row: Row<TData>) => void;
  onCellClick?: (cell: Cell<TData, TValue>) => void;
  hideFooter?: boolean;
  rowsPerPageOptions?: number[];
  name: string;
  size?: "lg" | "md" | "sm";
  hideEmptyMessage?: boolean;
  emptyMessage?: string;
};

export const DataTable = <TData, TValue>({
  table, // Table instance comes from the `useTable` hook
  columns,
  variant,
  onRowClick,
  onCellClick,
  hideFooter,
  rowsPerPageOptions,
  name,
  size,
  hideEmptyMessage = false,
  emptyMessage,
}: DataTableProps<TData, TValue>) => {
  const isShowingAllRows = (table.options.meta as { showAllRows?: boolean })
    ?.showAllRows;
  const shouldHideFooter = isShowingAllRows || hideFooter;

  return (
    <div className="flex flex-col w-full gap-10 gap-frame-border">
      <div
        className={cn(
          "overflow-scroll rounded-lg shadow-sm border border-muted [&_tr]:border-t"
        )}
      >
        <Table
          variant={variant}
          className={cn({
            "[&_tr]:h-12": size === "lg",
            "[&_tr]:h-11 [&_td]:py-0 [&_th]:px-2 [&_td]:px-2 [&_th:first-child]:px-4 [&_td:first-child]:px-4 [&_th:last-child]:pr-4 [&_td:last-child]:pr-4":
              size === "md",
            // "[&_tr]:h-12 [&_tr>td]:py-0": size === "sm",
          })}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return header.isPlaceholder ? null : (
                    <Fragment key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length
              ? table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className="group relative"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isCheckbox = cell.column.id === "select";
                      const isActions = cell.column.id === "actions";
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            cell.column.getIsSorted() &&
                              "bg-muted/30 border-x-[1.5px] border-x-muted/80",
                            isCheckbox && "w-16",
                            isActions && "w-32"
                          )}
                          onClick={
                            onCellClick
                              ? () => onCellClick(cell as Cell<TData, TValue>)
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                    {variant === "clickable" && (
                      <td className="hidden group-hover:flex items-center justify-start pl-5 absolute left-0 top-0 w-1/6 h-full bg-gradient-to-l from-transparent to-background pointer-events-none">
                        <Icon
                          name="arrow-right"
                          className="animate-in fade-in slide-in-from-left-14 text-lg"
                        />
                      </td>
                    )}
                  </TableRow>
                ))
              : !hideEmptyMessage && (
                  <TableRow>
                    <TableCell
                      colSpan={columns?.length || 1}
                      className="h-16 text-center"
                    >
                      {emptyMessage || "No hay resultados."}
                    </TableCell>
                  </TableRow>
                )}
          </TableBody>
        </Table>
      </div>

      {!shouldHideFooter && (
        <DataTablePagination
          table={table}
          rowsPerPageOptions={rowsPerPageOptions}
          name={name}
        />
      )}
    </div>
  );
};

DataTable.displayName = "DataTable";

export const DataTableSkeleton = ({
  rows = 10,
  className,
}: {
  rows?: number;
  className?: string;
}) => {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton
          key={index}
          className="h-[46px] w-full rounded-md opacity-80"
        />
      ))}
    </div>
  );
};
